/**
 * A receipt line drawn as dots, for a printer that cannot spell it
 * (escpos.ts). Drawn with the tablet's own fonts, which have every script
 * Android does, so a line in Arabic or Thai prints as it reads on screen.
 */

import type { PrintLine } from '@restaurant/ui';

import type { Raster } from './escpos';

/** Right-to-left scripts: Hebrew, Arabic, Syriac, Thaana, N'Ko and their presentation forms. */
const RTL = /[\u0590-\u08ff\ufb1d-\ufdff\ufe70-\ufeff]/;

export function rasterizeLine(line: PrintLine, dots: number): Raster {
  // Twelve dots a character: 48 across 80mm paper, as text mode prints.
  const size = line.big ? 40 : 20;
  const height = line.big ? 48 : 24;
  const canvas = document.createElement('canvas');
  canvas.width = dots;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const widthBytes = Math.ceil(dots / 8);
  if (!ctx) return { widthBytes, height, data: new Uint8Array(widthBytes * height) };

  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, dots, height);
  ctx.fillStyle = '#000';
  ctx.textBaseline = 'middle';
  ctx.font = `${line.bold ? '700' : '400'} ${size}px ui-monospace, "Noto Sans Mono", "Droid Sans Mono", monospace`;
  const rtl = RTL.test(line.text);
  ctx.direction = rtl ? 'rtl' : 'ltr';
  if (line.align === 'center') {
    ctx.textAlign = 'center';
    ctx.fillText(line.text, dots / 2, height / 2, dots);
  } else if (rtl) {
    ctx.textAlign = 'right';
    ctx.fillText(line.text, dots, height / 2, dots);
  } else {
    ctx.textAlign = 'left';
    ctx.fillText(line.text, 0, height / 2, dots);
  }

  const pixels = ctx.getImageData(0, 0, dots, height).data;
  const data = new Uint8Array(widthBytes * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < dots; x++) {
      const i = (y * dots + x) * 4;
      const light = 0.299 * pixels[i]! + 0.587 * pixels[i + 1]! + 0.114 * pixels[i + 2]!;
      if (light < 128) data[y * widthBytes + (x >> 3)]! |= 0x80 >> (x & 7);
    }
  }
  return { widthBytes, height, data };
}
